/**
 * Client-side photo preparation: shrink to max 1600px on the long side with
 * a canvas before upload so meal photos stay small. Falls back to the
 * original file when the browser cannot decode it.
 */

export const PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;
export type PhotoType = (typeof PHOTO_TYPES)[number];
export const PHOTO_MAX_BYTES = 10 * 1024 * 1024;
export const PHOTO_MAX_PX = 1600;

export interface PreparedPhoto {
    blob: Blob;
    contentType: PhotoType;
}

function isPhotoType(t: string): t is PhotoType {
    return (PHOTO_TYPES as readonly string[]).includes(t);
}

function loadImage(file: File): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
        img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('decode')); };
        img.src = url;
    });
}

function toBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob | null> {
    return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

export async function preparePhoto(file: File, maxPx = PHOTO_MAX_PX): Promise<PreparedPhoto> {
    const original: PreparedPhoto | null = isPhotoType(file.type) ? { blob: file, contentType: file.type } : null;

    try {
        const img = await loadImage(file);
        const w = img.naturalWidth;
        const h = img.naturalHeight;
        if (!w || !h) throw new Error('empty');
        const scale = Math.min(1, maxPx / Math.max(w, h));
        // Already small and already an accepted type: send as is.
        if (scale === 1 && original && file.size <= PHOTO_MAX_BYTES) return original;

        const canvas = document.createElement('canvas');
        canvas.width = Math.round(w * scale);
        canvas.height = Math.round(h * scale);
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('canvas');
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        // PNG stays PNG (screenshots); everything else becomes JPEG for size.
        const contentType: PhotoType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
        const blob = await toBlob(canvas, contentType, 0.85);
        if (!blob) throw new Error('encode');
        return { blob, contentType };
    } catch {
        if (original && file.size <= PHOTO_MAX_BYTES) return original;
        throw new Error('Kuvaa ei voitu käsitellä.');
    }
}
